/**
 * O manual da mesa — o texto, as imagens e os vídeos, num dado só.
 *
 * Ajuda é CONTEÚDO, e conteúdo não pode morar dentro de componente: quem
 * escreve o manual do escritório não deveria precisar abrir JSX para trocar uma
 * frase, e a busca precisa varrer tudo sem instanciar tela nenhuma. Aqui está o
 * manual inteiro em forma de dado; `Ajuda.jsx` só pinta, e `busca.js` só
 * procura.
 *
 * Regras do que entra:
 *
 *   1. **Nada de etapa, verba ou lista de opções cravada.** O esquema do CRM é
 *      configurável em tempo de execução — o manual explica ONDE se muda a
 *      etapa, não quais etapas existem hoje neste escritório.
 *   2. **Imagem é print do sistema de verdade**, gerado por
 *      `web/testes/ajuda-imagens.spec.ts` contra a mesa semeada. Desenho
 *      inventado envelhece sem ninguém perceber; print quebrado aparece na
 *      primeira olhada (e `ajuda.test.jsx` reprova arquivo que não existe).
 *   3. **Vídeo é recorte da jornada gravada** — a mesma que a auditoria filma
 *      ponta a ponta. O escritório vê o fluxo real, não uma encenação.
 *
 * Blocos aceitos: `p` (parágrafo), `passos` (lista numerada), `itens` (lista),
 * `imagem`, `video`, `aviso`, `atalhos`, `tabela` e `link` (para um lugar da
 * própria mesa).
 */

/** Onde as imagens e os vídeos do manual moram. Servidos como estático. */
export const PASTA = "/ajuda";

const p = (texto) => ({ t: "p", texto });
const passos = (...itens) => ({ t: "passos", itens });
const itens = (...lista) => ({ t: "itens", itens: lista });
const imagem = (arquivo, legenda) => ({ t: "imagem", arquivo, legenda });
const video = (arquivo, legenda) => ({ t: "video", arquivo, legenda });
const aviso = (texto, tom = "atencao") => ({ t: "aviso", texto, tom });
const atalhos = (...pares) => ({ t: "atalhos", itens: pares.map(([teclas, oque]) => ({ teclas, oque })) });
const tabela = (cabecalho, linhas) => ({ t: "tabela", cabecalho, linhas });
const link = (para, rotulo, explica) => ({ t: "link", para, rotulo, explica });

export const MANUAL = [
  /* ------------------------------------------------------------ começar */
  {
    id: "primeiros-passos",
    titulo: "Primeiros passos",
    resumo:
      "O primeiro acesso, os passos que põem o setor de pé (ou ensinam a mesa a quem atende), a demonstração e o caminho de um caso.",
    icone: "brilho",
    secoes: [
      {
        id: "primeiro-acesso",
        titulo: "O primeiro acesso",
        blocos: [
          passos(
            "Quem cuida da equipe cria o seu acesso e passa a senha provisória por um canal seguro.",
            "Você entra com o e-mail e a senha provisória.",
            "A mesa pede uma senha sua antes de abrir qualquer coisa: doze caracteres ou mais, sem o seu nome nem o seu e-mail. A barra embaixo do campo diz, enquanto você digita, se o sistema vai aceitar.",
            "Trocada a senha, as outras sessões com a sua conta são encerradas e a mesa abre nos Primeiros passos.",
          ),
          aviso(
            "A senha provisória é conhecida por quem a criou — por isso ela não abre nada além da tela de troca. Esqueceu a sua? Quem cuida da equipe redefine, e você troca de novo na entrada.",
            "nota",
          ),
        ],
      },
      {
        id: "seus-passos",
        titulo: "Os seus passos — marcados pelo que o sistema vê",
        blocos: [
          p(
            "A tela Primeiros passos diz em que setor você está, com qual papel e o que falta. Não há botão de “feito”: cada passo se marca sozinho quando o sistema vê a coisa acontecer, e a lista some da boas-vindas quando o último se cumpre.",
          ),
          tabela(
            ["Quem configura o setor", "Se cumpre quando"],
            [
              ["Conectar o WhatsApp do setor", "um número do setor está conectado de verdade"],
              ["Cadastrar a equipe e os papéis", "o setor tem pelo menos duas pessoas"],
              ["Conferir o horário de atendimento", "o horário foi gravado em Ajustes"],
              ["Escolher quanto a IA faz", "alguém escolheu Sem IA, Copiloto ou Piloto"],
              ["Revisar os agentes", "um agente foi revisado ou já trabalhou — no Sem IA, não é pedido"],
              ["Trazer os modelos de contrato", "existe um modelo de contrato ativo"],
            ],
          ),
          tabela(
            ["Quem atende", "Se cumpre quando"],
            [
              ["Responder a primeira conversa", "você mandou uma mensagem a um cliente (nota interna não conta)"],
              ["Andar um caso pelo AGORA", "você moveu um caso de etapa"],
              ["Conferir um documento", "você disse se um documento serve"],
              ["Abrir a paleta", "você abriu a paleta pelo atalho ou pela lupa"],
            ],
          ),
          p("Cada passo leva direto ao lugar onde se resolve. Só aparece o passo que o seu acesso permite fazer."),
          link("/comecar", "Abrir os primeiros passos", "a lista do seu papel, no setor em que você está"),
        ],
      },
      {
        id: "demonstracao",
        titulo: "A demonstração guiada",
        blocos: [
          p(
            "Uma seta anda pela mesa e explica cada parte: a lista, as tarefas, a IA, e — com um atendimento na mesa — o AGORA, a leitura da IA, a ficha, a resposta e a petição. Nada é enviado nem gravado; o Esc ou qualquer toque encerram.",
          ),
          p("Ela abre pela paleta (digite “demonstração”), pelos Primeiros passos e por este link."),
          link("/atendimentos?demonstracao=1", "Ver a demonstração", "a mesa passeia sozinha"),
        ],
      },
      {
        id: "o-que-e",
        titulo: "O que é a mesa de atendimento",
        blocos: [
          p(
            "A mesa é o lugar onde o escritório atende. Ela junta, numa tela só, a conversa com o cliente, a ficha do caso, os documentos, os prazos e a petição. O que existe aqui é o manual do escritório virado software: do primeiro “oi” no WhatsApp até o número do processo voltando para o cliente.",
          ),
          p(
            "Nada acontece em dois lugares. A conversa que você lê é a mesma que o cliente vê no celular dele; o documento que você aprova é o que a petição vai citar; o prazo que aparece na agenda é a tarefa que alguém tem para fazer.",
          ),
          imagem("mesa-visao-geral.png", "A mesa aberta num atendimento: trilho à esquerda, conversa no meio, o caso à direita."),
        ],
      },
      {
        id: "tres-colunas",
        titulo: "As três colunas",
        blocos: [
          p("Toda a mesa é lida da esquerda para a direita, e cada coluna responde a uma pergunta diferente."),
          tabela(
            ["Coluna", "Pergunta que ela responde"],
            [
              ["Trilho (extrema esquerda)", "Em que área do escritório eu estou?"],
              ["Lista de atendimentos", "Com quem eu preciso falar agora?"],
              ["Conversa (meio)", "O que foi dito, e o que o sistema fez?"],
              ["Painel do caso (direita)", "Em que pé está este caso, e qual é o próximo passo?"],
            ],
          ),
          p(
            "No celular as três não cabem lado a lado: a lista vira a tela inicial, a conversa abre por cima e o painel do caso vira uma gaveta que sobe. É a mesma mesa, dobrada.",
          ),
        ],
      },
      {
        id: "o-caminho-do-caso",
        titulo: "O caminho de um caso, do começo ao fim",
        blocos: [
          p(
            "Este é o fluxo do manual do escritório. Cada etapa tem uma tela, e a mesa empurra o caso de uma para a outra — várias delas sozinha.",
          ),
          passos(
            "O cliente manda mensagem (WhatsApp, Instagram ou um anúncio) e vira um atendimento na lista, sem ninguém cadastrar nada.",
            "Alguém atende, entende o caso e preenche a ficha: tese, empresa, datas e salário.",
            "Com a ficha de pé, o contrato vai para assinatura digital e os links chegam na conversa do cliente.",
            "Assinado o contrato, o sistema abre a pasta do cliente, move a etapa e monta o checklist de documentos daquela tese.",
            "O cliente manda os documentos pelo WhatsApp; a mesa baixa, classifica e põe cada um na fila de conferência.",
            "Alguém confere um por um, com o documento grande na tela.",
            "Com o checklist aprovado, o portão do manual cria a peça na esteira e abre o caso no Astrea.",
            "A IA lê a conversa, separa os fatos e monta a inicial no papel timbrado; o advogado revisa e edita.",
            "Protocolada a peça, o número entra na ficha, a etapa vai para o fim do funil e o aviso volta para o cliente.",
          ),
          video("jornada.mp4", "A jornada inteira, gravada no sistema real: do primeiro oi ao processo protocolado."),
        ],
      },
      {
        id: "quem-ve-o-que",
        titulo: "Quem vê o quê",
        blocos: [
          p(
            "O que aparece no trilho depende do que a sua conta pode fazer. Permissão aqui é ação, não área: “ver leads”, “escrever na esteira”, “aprovar documento”. Se um lugar não aparece para você, é porque o seu papel não tem a ação que ele exige — e quem muda isso é Equipe e acessos.",
          ),
          link("/equipe", "Equipe e acessos", "onde se define o papel de cada pessoa e o que cada papel alcança"),
        ],
      },
    ],
  },

  /* ------------------------------------------------------- navegação */
  {
    id: "navegar",
    titulo: "Navegar e buscar",
    resumo: "Como andar pela mesa sem se perder: trilho, endereços, a paleta do Ctrl+K e as buscas de cada tela.",
    icone: "busca",
    secoes: [
      {
        id: "trilho",
        titulo: "O trilho da esquerda",
        blocos: [
          p(
            "O trilho é o mapa do escritório, em três grupos: o dia (atendimentos, tarefas e agenda), o acervo (reuniões, documentos, respostas rápidas) e o escritório (IA do atendimento, equipe, ajustes e, para quem responde pelos setores, Setores).",
          ),
          p(
            "Ele abre ao encostar o mouse e fecha ao sair. Se você prefere ele sempre aberto, o alfinete no topo prende — a escolha fica gravada no seu navegador.",
          ),
          imagem("trilho.png", "O trilho aberto, com os três grupos e o contador de pendências."),
        ],
      },
      {
        id: "enderecos",
        titulo: "Cada lugar tem endereço",
        blocos: [
          p(
            "A navegação mora na URL. Isso não é detalhe técnico: significa que você pode mandar um lugar da mesa por mensagem, fixar na aba do navegador e voltar exatamente onde estava depois de recarregar.",
          ),
          tabela(
            ["Endereço", "O que abre"],
            [
              ["/atendimentos", "a lista de atendimentos"],
              ["/atendimentos/12", "o atendimento número 12, com a conversa aberta"],
              ["/atendimentos/12?painel=1", "o mesmo atendimento com a ficha do caso aberta"],
              ["/atendimentos/12?peticao=1", "a petição desse caso"],
              ["/tarefas", "o quadro de tarefas"],
              ["/tarefas?visao=semana", "a agenda da semana — os mesmos prazos no eixo do tempo"],
            ],
          ),
          p(
            "A regra: o que é LUGAR vira caminho; o que é recorte da mesma tela (busca, filtro, aba, painel) vira query. Por isso o botão voltar do navegador desfaz o que você fez.",
          ),
        ],
      },
      {
        id: "paleta",
        titulo: "Ir para qualquer lugar com Ctrl+K",
        blocos: [
          p(
            "A paleta é o caminho mais rápido da mesa: ela encontra área, cliente e caso no mesmo campo. Digite parte do nome, do telefone ou do número do processo.",
          ),
          p(
            "Ela também FAZ: novo atendimento, puxar o próximo da fila, nova tarefa, cadastrar pessoa, os primeiros passos e a demonstração. Digite o nome de outro setor para ir para lá, ou “modo” para escolher quanto a IA faz — cada verbo só aparece para quem pode fazê-lo.",
          ),
          atalhos(
            ["Ctrl+K", "abre a paleta (Cmd+K no Mac)"],
            ["↑ ↓", "anda pelos resultados"],
            ["Enter", "vai para o resultado escolhido"],
            ["Esc", "fecha"],
          ),
          imagem("paleta.png", "A paleta aberta, com áreas e clientes no mesmo resultado."),
        ],
      },
      {
        id: "buscas",
        titulo: "As buscas de cada tela",
        blocos: [
          p(
            "Cada tela tem a sua busca, e todas ignoram acento: procurar por “rescisao” acha “rescisão”. Elas procuram no servidor, e não só no que está carregado na tela — a conta que aparece é a conta de verdade.",
          ),
          itens(
            "Atendimentos: nome, telefone, etapa e o texto das mensagens.",
            "Tarefas: título da tarefa, responsável e o cliente do caso.",
            "Documentos: nome do arquivo e o cliente dono da pasta.",
            "Respostas rápidas: atalho, categoria e o texto da resposta.",
            "Dentro de uma conversa: a lupa do cabeçalho procura só naquele atendimento e salta de resultado em resultado.",
          ),
        ],
      },
    ],
  },

  /* ---------------------------------------------------- atendimentos */
  {
    id: "atendimentos",
    titulo: "Atendimentos e conversa",
    resumo: "A tela onde o escritório passa o dia: a lista, os filtros, a conversa, o rascunho e o histórico do caso.",
    icone: "chats",
    secoes: [
      {
        id: "lista",
        titulo: "A lista de atendimentos",
        blocos: [
          p(
            "Cada linha é um caso. Ela mostra o nome, a última mensagem, a etapa em que o caso está, quem é o responsável e há quanto tempo ele não anda.",
          ),
          p(
            "As pastilhas no topo são filtros combináveis: não lidas, minhas, sem responsável, em risco, com documento pendente, por canal. Elas entram na URL — o recorte que você montou pode ser mandado para alguém.",
          ),
          imagem("lista-atendimentos.png", "A lista com os filtros do topo e a contagem de cada um."),
          aviso(
            "A contagem das pastilhas vem do servidor, não da página carregada. Contar na tela conta só o que já desceu — e foi um erro que esta mesa já cometeu.",
            "nota",
          ),
        ],
      },
      {
        id: "o-que-cada-linha-diz",
        titulo: "O que cada pedaço da linha quer dizer",
        blocos: [
          p("A linha do atendimento é densa de propósito: ela responde em um olhar se aquele caso precisa de você agora."),
          tabela(
            ["Onde", "O que é", "Como ler"],
            [
              ["Círculo com iniciais", "o cliente", "a cor é derivada do nome — a mesma pessoa tem sempre a mesma cor"],
              ["Nome em negrito", "titular do caso", "negrito mais forte quando há mensagem não lida"],
              ["Linha cinza abaixo", "última mensagem", "começa com “Você:” quando quem falou por último foi o escritório"],
              ["Pastilha colorida", "tese do caso", "a cor vem das teses do escritório, em Ajustes"],
              ["Pastilha clara", "etapa do funil", "é a mesma etapa que aparece no painel e no trilho do processo"],
              ["Bolinha à direita", "responsável", "iniciais de quem atende; vazia quando ninguém pegou"],
              ["Número", "não lidas", "quantas mensagens do cliente ainda não foram abertas"],
              ["Relógio/dias", "tempo parado", "há quanto tempo o caso não anda — é o que a régua de cobrança conta"],
            ],
          ),
        ],
      },
      {
        id: "filtros",
        titulo: "Os filtros, um a um",
        blocos: [
          p("As pastilhas do topo se combinam: “minhas” + “em risco” mostra o que é seu e está parado demais."),
          tabela(
            ["Filtro", "Traz"],
            [
              ["Não lidas", "casos com mensagem do cliente ainda não aberta"],
              ["Meus", "casos em que você é o responsável"],
              ["Sem responsável", "o que chegou e ninguém pegou — a fila de verdade do escritório"],
              ["Em risco", "parados além do que a régua tolera para a etapa"],
              ["Documento pendente", "com item do checklist esperando o cliente ou esperando conferência"],
              ["Canal", "só WhatsApp, só Instagram, só quem veio de anúncio"],
              ["Etapa", "um ponto do funil — útil para varrer “aguardando documentos” de uma vez"],
            ],
          ),
          p(
            "O que você montou fica no endereço da página. Um recorte que você usa todo dia pode virar um favorito do navegador — e um recorte que precisa de outra pessoa vai por mensagem.",
          ),
        ],
      },
      {
        id: "conversa",
        titulo: "A conversa",
        blocos: [
          p(
            "No meio fica a conversa, igual à do WhatsApp: bolhas do cliente à esquerda, do escritório à direita, com hora, tique de entrega e resposta citada. Foto, áudio, PDF e localização aparecem na própria bolha; áudio do cliente vem com a transcrição embaixo.",
          ),
          p(
            "A conversa recebe ao vivo. Quando o cliente responde, a mensagem entra sem recarregar — e sem apagar o que você estava escrevendo.",
          ),
          imagem("conversa.png", "Uma conversa com mensagens do cliente, resposta do escritório e um documento recebido."),
          video("clip-lead.mp4", "O lead chegando pelo anúncio, a conversa ao vivo e a primeira resposta do escritório."),
        ],
      },
      {
        id: "marcos",
        titulo: "O que o escritório FEZ, dentro da conversa",
        blocos: [
          p(
            "Entre as mensagens aparecem cartões diferentes das bolhas: são os marcos do caso. Contrato enviado, documento assinado, documento aprovado, etapa movida, peça criada, caso aberto no Astrea, processo protocolado.",
          ),
          p(
            "Eles têm desenho próprio de propósito: a conversa tem valor probatório, e o que o sistema fez não pode se confundir com o que alguém disse. Clicar no marco da peça abre a petição.",
          ),
          imagem("marcos.png", "Marcos do caso entre as mensagens: assinatura, documento aprovado e peça na esteira."),
        ],
      },
      {
        id: "escrever",
        titulo: "Escrever, anexar e responder",
        blocos: [
          passos(
            "Escreva no campo de baixo. O rascunho é seu: ele fica salvo mesmo se a conversa receber mensagem nova.",
            "Enter envia; Shift+Enter quebra linha.",
            "A barra “/” abre as respostas prontas do escritório — digite o atalho e o texto entra com as variáveis já resolvidas.",
            "O clipe anexa arquivo; o microfone grava áudio.",
            "Para responder uma mensagem específica, use o menu da bolha e “Responder”: a citação vai junto.",
          ),
          aviso(
            "Quando o WhatsApp do escritório está fora do ar, a mesa grava a mensagem e avisa na hora que ela não chegou ao cliente — com um botão para enviar depois. Mensagem que “sumiu” sem aviso seria pior que erro.",
          ),
          atalhos(
            ["Enter", "envia a mensagem"],
            ["Shift+Enter", "quebra a linha"],
            ["/", "abre as respostas rápidas"],
            ["Esc", "fecha o painel ou a caixa aberta"],
          ),
        ],
      },
      {
        id: "fato",
        titulo: "Marcar um trecho como fato da petição",
        blocos: [
          p(
            "Quando o cliente conta algo que vai para a inicial — a data da demissão, o valor do salário, a hora extra —, marque ali mesmo. O trecho vira fato do caso, com a origem (quem disse e quando) presa nele.",
          ),
          passos(
            "Selecione o texto na bolha, ou use o menu da bolha.",
            "Escolha “marcar como fato”.",
            "O fato aparece na coluna de fatos da petição, pronto para entrar na peça.",
          ),
          p("A tela de Provas separada não existe mais: marcar acontece onde a conversa está."),
        ],
      },
    ],
  },

  /* ----------------------------------------------------------- caso */
  {
    id: "caso",
    titulo: "A ficha e o funil",
    resumo: "O painel do caso: dados do cliente, etapa, próximo passo, checklist e o que a IA já leu.",
    icone: "peca",
    secoes: [
      {
        id: "painel",
        titulo: "O painel do caso",
        blocos: [
          p(
            "À direita fica tudo sobre o caso aberto, numa coluna só e sempre aberta a partir de 880px de tela. Em cima, quem é o cliente e o que fazer agora; abaixo, um cartão para cada assunto do caso — petição, documentos, prazos, ficha, etiquetas, reuniões, etapa e histórico. Cada cartão diz o número que responde de relance (“4/4”, “2 atrasadas”) e o toque abre o assunto inteiro.",
          ),
          aviso(
            "Não há mais abas “Caso” e “Reunião”: a segunda ficava vazia na maioria dos casos e escondia a gravação em curso de quem estava na ficha. O gravador agora mora no alto da coluna, e se recolhe a uma linha fora da hora dele.",
            "nota",
          ),
          imagem("ficha-funil.png", "O painel do caso com o próximo passo, a etapa e a ficha."),
        ],
      },
      {
        id: "trilho-do-processo",
        titulo: "O trilho do processo, no alto da conversa",
        blocos: [
          p(
            "Acima da conversa há uma régua com as etapas do funil e uma bolinha marcando onde o caso está. É a resposta imediata para “em que pé isto está?”, sem abrir o painel.",
          ),
          imagem("trilho-processo.png", "O trilho do processo no cabeçalho, com a etapa atual destacada."),
        ],
      },
      {
        id: "mover-etapa",
        titulo: "Mover a etapa",
        blocos: [
          passos(
            "No painel do caso, toque na linha “Etapa” e escolha a etapa de destino — ou toque no segmento do trilho, em cima da conversa.",
            "A mesa pergunta antes de gravar — escrita no funil não acontece por engano.",
            "Confirmada, a etapa muda e a trilha guarda quem moveu e quando.",
          ),
          aviso(
            "As etapas não são fixas no código: elas vêm do esquema do escritório e podem ser renomeadas ou reordenadas em Ajustes. Por isso este manual não lista as etapas — a sua mesa mostra as suas.",
            "nota",
          ),
        ],
      },
      {
        id: "etiquetas",
        titulo: "Etiquetas: o recorte que o escritório inventa",
        blocos: [
          p(
            "Etapa diz onde o caso está, tese diz do que ele trata e responsável diz de quem ele é. A etiqueta é o quarto recorte — o que muda toda semana e não cabe nos outros três: “urgente”, “audiência marcada”, “indicação do Dr. Paulo”, “aguardando perícia”. Antes disso, esse tipo de aviso virava nota na trilha, onde não filtra nem conta.",
          ),
          p(
            "Ela não tem cadastro em outra tela: a etiqueta nasce escrita. E o mesmo lugar onde se põe é onde se renomeia e se apaga o rótulo do escritório inteiro.",
          ),
          passos(
            "Abra o cartão “Etiquetas”, na coluna do caso — ou clique com o botão direito numa conversa da lista e escolha “Etiquetas”, que etiqueta sem abrir o caso.",
            "Marque uma da lista do escritório, ou escreva um nome novo e aperte Enter.",
            "O número ao lado de cada etiqueta é em quantos casos ela está, e a barra compara com a mais usada — é por ela que se vê qual rótulo virou bagunça.",
            "Clicar numa etiqueta do caso filtra a mesa por ela: “me mostra todos os urgentes”.",
          ),
          atalhos(["Enter", "cria a etiqueta escrita e põe no caso"], ["Esc", "limpa o que foi digitado"]),
          aviso(
            "Renomear e apagar valem para TODOS os casos que têm aquele rótulo, e a mesa diz quantos são antes de perguntar. Tirar a etiqueta de um caso, não: é o gesto do dia e se desfaz no mesmo toque, então não pergunta.",
            "nota",
          ),
          aviso(
            "“Urgente” e “urgente” são a mesma etiqueta: a comparação ignora acento e caixa. Sem isso o catálogo ganhava três linhas para um assunto e o filtro achava um terço dos casos.",
            "nota",
          ),
        ],
      },
      {
        id: "proximo-passo",
        titulo: "O próximo passo",
        blocos: [
          p(
            "No topo do painel a mesa diz qual é a ação que faz o caso andar agora: responder o cliente, preencher a ficha, pedir o que falta, conferir documentos, aprovar o checklist, gerar a minuta. Cada uma é um botão, com a permissão que ela exige já verificada.",
          ),
          p(
            "O passo que não tem desfazer avisa antes. “Aprovar o checklist” é o exemplo: ele cria a peça, abre o caso no Astrea e move a etapa de uma vez só.",
          ),
        ],
      },
      {
        id: "copiloto",
        titulo: "O assistente do caso",
        blocos: [
          p(
            "O cartão escuro do painel é a leitura da IA sobre o caso: o resumo, os riscos e o que ela sugere como próxima ação. Ele lê a conversa e a ficha — o que é fato do escritório (prazo, documento faltando, tempo de resposta) sai do banco, nunca do modelo.",
          ),
          p("“Reler” refaz a leitura depois de algo mudar. A hora da última leitura fica escrita ali."),
          imagem("copiloto.png", "O assistente resumindo o caso, com êxito estimado e próximas ações sugeridas."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------- contrato */
  {
    id: "contrato",
    titulo: "Contrato e assinatura",
    resumo: "Como o pacote de assinatura sai, como o cliente recebe os links e o que acontece quando ele assina.",
    icone: "documento",
    secoes: [
      {
        id: "enviar",
        titulo: "Mandar o contrato para assinatura",
        blocos: [
          p(
            "São três documentos, sempre: contrato de honorários, procuração e declaração de hipossuficiência. Eles nascem NO CRM com os dados da ficha — ninguém redigita nome e CPF em outro sistema, e cada signatário recebe o próprio link.",
          ),
          passos(
            "Com a tese confirmada, nome completo, CPF e os campos que o modelo imprime na ficha, o contrato pode sair.",
            "A mesa cria os três documentos e um link de assinatura para cada um.",
            "O texto vai para a conversa do cliente — com os três links, um por documento.",
          ),
          tabela(
            ["Modo do setor", "Quem manda o contrato"],
            [
              ["Sem IA", "uma pessoa, pelo passo “Enviar contrato” no painel do caso"],
              ["Copiloto", "o sistema, assim que uma pessoa confirma a tese — a confirmação é o portão"],
              ["Piloto automático", "a IA, depois de confirmar a tese"],
            ],
          ),
          video("clip-contrato.mp4", "O contrato saindo e os links chegando na conversa da cliente."),
          aviso(
            "Contrato já enviado não sai de novo sozinho — reenviar é outra ação. Se o provedor falhar, o caso volta para uma pessoa com o motivo.",
          ),
        ],
      },
      {
        id: "assinou",
        titulo: "Quando o cliente assina",
        blocos: [
          p(
            "Cada assinatura aparece na conversa na hora: “Contrato de honorários assinado pelo cliente — 1 de 3”. Você acompanha sem sair da tela e sem perguntar ao cliente.",
          ),
          p("Quando os três estiverem assinados, o gatilho do manual dispara sozinho:"),
          itens(
            "a pasta do cliente é criada;",
            "a etapa vai para “aguardando documentos”;",
            "o checklist da tese abre com os documentos que aquela tese exige;",
            "a régua de cobrança começa a contar.",
          ),
          imagem("assinaturas.png", "As três assinaturas registradas na conversa, uma a uma."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------ documentos */
  {
    id: "documentos-do-caso",
    titulo: "Documentos do cliente",
    resumo: "Checklist, cobrança automática, o que chega pelo WhatsApp e a conferência um por um.",
    icone: "files",
    secoes: [
      {
        id: "checklist",
        titulo: "O checklist da tese",
        blocos: [
          p(
            "Cada tese pede um conjunto de documentos, e o checklist abre sozinho quando o contrato fecha. Cada item tem um estado: faltando, chegou (esperando conferência), aprovado, ilegível ou dispensado.",
          ),
          imagem("checklist.png", "O checklist do caso com o estado de cada documento."),
        ],
      },
      {
        id: "cobranca",
        titulo: "Cobrar o que falta",
        blocos: [
          p(
            "A régua de cobrança do escritório é D+2, D+5 e D+10: o texto muda de tom conforme o caso envelhece na etapa. A mesa escreve; quem manda é gente.",
          ),
          passos(
            "No painel do caso, clique em “Pedir o que falta”.",
            "O texto da régua — já com a lista do que falta — cai no campo de escrever.",
            "Leia, ajuste se quiser, e envie.",
          ),
          video("clip-documentos.mp4", "A cobrança sendo escrita pela régua e os documentos chegando pelo WhatsApp."),
          aviso("A cobrança nunca sai sozinha do campo: a última leitura antes do cliente é sempre de uma pessoa.", "nota"),
        ],
      },
      {
        id: "chegando",
        titulo: "O documento chegando",
        blocos: [
          p(
            "Quando o cliente manda a foto pelo WhatsApp, a mesa baixa o arquivo, guarda na pasta dele, lê o que dá para ler e classifica contra o checklist da tese. O item passa a “chegou — esperando conferência”, e um marco aparece na conversa.",
          ),
          p(
            "A classificação é uma sugestão com grau de confiança, não uma decisão: quem decide se o documento serve é o atendente.",
          ),
        ],
      },
      {
        id: "conferir",
        titulo: "Conferir um por um",
        blocos: [
          p(
            "A conferência é a ação mais repetida do escritório — quatro por caso, todo dia. Ela acontece numa tela só: o documento grande à esquerda, o que o sistema já sabe à direita, três decisões embaixo.",
          ),
          passos(
            "No painel do caso, clique em “Conferir N documento(s) na tela”.",
            "Olhe o documento. A avaliação automática diz o que o sistema achou que é, e com quanta confiança.",
            "Decida: aprovar, ilegível ou dispensar.",
            "A fila anda sozinha para o próximo; no fim, ela diz que acabou.",
          ),
          atalhos(
            ["1", "aprova"],
            ["2", "marca como ilegível e devolve para o cliente"],
            ["3", "dispensa o documento neste caso"],
            ["← →", "anda entre os documentos"],
            ["Esc", "fecha a conferência"],
          ),
          imagem("conferir-documentos.png", "A conferência com o documento na tela e as três decisões."),
          tabela(
            ["Decisão", "O que acontece"],
            [
              ["Aprovar", "o documento vira prova da peça e conta para o portão do checklist"],
              ["Ilegível", "volta para o cliente e entra de novo na régua de cobrança"],
              ["Dispensar", "sai do checklist deste caso — é o único que não pede nada a ninguém"],
            ],
          ),
        ],
      },
      {
        id: "portao",
        titulo: "O portão do manual",
        blocos: [
          p(
            "Com todos os documentos resolvidos, aparece “Aprovar o checklist”. Ele é o portão: cria a peça na esteira, abre o caso no Astrea e move a etapa — de uma vez, numa transação só.",
          ),
          aviso("Não tem desfazer. Por isso a mesa pergunta antes."),
        ],
      },
    ],
  },

  /* ---------------------------------------------------------- peça */
  {
    id: "peticao",
    titulo: "A petição inicial",
    resumo: "Fatos extraídos da conversa, minuta escrita pela IA sobre o acervo da casa, edição no .docx e protocolo.",
    icone: "peca",
    secoes: [
      {
        id: "abrir",
        titulo: "Abrir a petição",
        blocos: [
          p(
            "A petição abre de três lugares: pelo passo “Gerar a minuta” no painel, pelo marco da peça na conversa, ou pelo endereço direto do caso com `?peticao=1`.",
          ),
          p(
            "A tela tem duas colunas: à esquerda o documento como ele vai para o fórum (papel timbrado, margens, rodapé), à direita os fatos do caso.",
          ),
          imagem("peticao-fatos.png", "A petição aberta com a coluna de fatos à direita."),
        ],
      },
      {
        id: "fatos",
        titulo: "Extrair os fatos da conversa",
        blocos: [
          p(
            "A petição não nasce da ficha: nasce do que o cliente contou. “Extrair fatos da conversa” manda a IA ler o atendimento inteiro e devolver cada fato separado por espécie — fato, prova ou pedido — com a procedência (a mensagem de onde saiu).",
          ),
          passos(
            "Clique em “Extrair fatos da conversa”.",
            "Leia a lista. Cada cartão traz o rótulo, a frase que pode ir para a peça e de onde ela veio.",
            "Marque o que entra. O que estiver marcado é o que a montagem usa.",
            "Fato marcado à mão na conversa aparece aqui do mesmo jeito — é o mesmo dado.",
          ),
          aviso("A IA não inventa: sem base na conversa ou na ficha, o item não é devolvido.", "nota"),
        ],
      },
      {
        id: "montar",
        titulo: "Montar a minuta",
        blocos: [
          p(
            "“Montar do acervo” escreve a inicial usando como forma as peças que o escritório já protocolou — não um modelo genérico. O texto nasce direto na folha timbrada, linha por linha, e você acompanha enquanto é escrito.",
          ),
          p("Leva de um a dois minutos: a peça é longa. Cancelar no meio devolve o documento ao que estava antes."),
          video("clip-peca.mp4", "A minuta sendo escrita na folha, com os fatos marcados como base."),
        ],
      },
      {
        id: "editar",
        titulo: "Editar o documento",
        blocos: [
          p(
            "O que está na tela é o .docx de verdade, com a barra de ferramentas da casa: negrito, itálico, listas, alinhamento, estilo de parágrafo, busca no documento e zoom. Salvar grava uma versão nova, e a trilha guarda quem salvou.",
          ),
          itens(
            "“Inserir na peça” leva um fato para dentro do texto, no cursor.",
            "A completude mostra quanto da peça já está sustentada por fato marcado.",
            "Baixar entrega o .docx como ele está.",
          ),
          imagem("editor-peca.png", "O editor com a peça no papel timbrado e a barra de ferramentas."),
        ],
      },
      {
        id: "barra-do-editor",
        titulo: "A barra de ferramentas, botão a botão",
        blocos: [
          tabela(
            ["Botão", "O que faz"],
            [
              ["Estilo do parágrafo", "título de seção, corpo do texto, citação — é o que dá a hierarquia da peça"],
              ["Fonte e tamanho", "a família do escritório vem do timbrado; mudar aqui vale só para o trecho selecionado"],
              ["N / I / S", "negrito, itálico, sublinhado"],
              ["Alinhamento", "esquerda, centro, direita e justificado — inicial vai justificada"],
              ["Entrelinha", "1, 1,5 ou 2 — o padrão do fórum costuma ser 1,5"],
              ["Listas", "com marcador e numerada, para os pedidos"],
              ["Link", "insere endereço no trecho selecionado"],
              ["Lupa", "busca dentro do documento, com salto entre ocorrências"],
              ["Ajustar", "o zoom da folha — não muda o documento, só a leitura"],
              ["⋯", "baixar o .docx e as opções menos usadas"],
            ],
          ),
          aviso(
            "Salvar grava uma VERSÃO: a anterior continua na trilha, com quem salvou e quando. Nada do que foi escrito se perde por cima.",
            "nota",
          ),
        ],
      },
      {
        id: "protocolo",
        titulo: "Revisão e protocolo",
        blocos: [
          passos(
            "A peça vai para revisão — o sócio lê e aponta o que precisa mudar.",
            "Liberada, ela fica “a protocolar”.",
            "Protocolada, informe o número do processo, o tribunal, a vara e o valor da causa.",
            "Numa transação só: o número entra na ficha, a etapa vai para o fim do funil e os prazos do caso fecham.",
            "O aviso com o número volta para o cliente, depois de aprovado na fila.",
          ),
          video("clip-protocolo.mp4", "O protocolo e o número do processo voltando para a ficha e para o cliente."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------- o dia */
  {
    id: "publicacoes",
    titulo: "Publicações dos diários",
    resumo: "A fila do que os diários oficiais publicaram sobre os processos do escritório, e como ela vira prazo no quadro.",
    icone: "publicacoes",
    secoes: [
      {
        id: "de-onde-vem",
        titulo: "De onde vem",
        blocos: [
          p(
            "As publicações são coletadas do DJEN — o Diário de Justiça Eletrônico Nacional, do CNJ. Desde maio de 2025 ele é o único diário que conta prazo no país, e cobre a comunicação dirigida de todos os tribunais estaduais, dos regionais do trabalho, dos federais, do STJ, do TST e do STM.",
          ),
          p(
            "A busca é feita por três caminhos ao mesmo tempo para cada advogado vigiado: pelo número da OAB, pelo nome do advogado e pelo nome dentro do texto. Não é redundância — o edital costuma vir sem OAB vinculada, e sem o terceiro caminho ele não apareceria.",
          ),
          p(
            "A coleta roda sozinha, todos os dias, sem ninguém abrir a tela: ler diário público é leitura, não escreve nada fora do CRM, e prazo perdido não volta. O botão “Buscar agora” existe para a pergunta pontual — “saiu algo hoje?” às cinco da tarde.",
          ),
        ],
      },
      {
        id: "quem-e-vigiado",
        titulo: "Quem o escritório vigia",
        blocos: [
          p(
            "A lista de advogados monitorados fica em Ajustes, no formato nome, dois pontos, número da OAB, barra e a sigla do estado — um por linha, separados por vírgula. Sem ninguém na lista não há o que procurar, e a tela diz isso em vez de mostrar uma lista vazia.",
          ),
          aviso(
            "O casamento é pela OAB; o nome completo é rede secundária. Procurar só pelo sobrenome traz publicação de outros advogados homônimos — dezenas num único dia de tribunal grande.",
          ),
        ],
      },
      {
        id: "tratar",
        titulo: "Tratar uma publicação",
        blocos: [
          p(
            "A tela abre nas não lidas, do mais recente para o mais antigo: é uma fila para zerar. Cada linha já diz o que falta nela — se nenhum caso do CRM foi encontrado com aquele número de processo, e se o prazo ainda não foi aberto.",
          ),
          passos(
            "Toque na linha para ler o teor inteiro, como o tribunal publicou.",
            "Se ela não achou o caso sozinha, procure o cliente pelo nome e ligue as duas coisas — a partir daí a publicação aparece na trilha do atendimento.",
            "Se o ato gera prazo, confira a data sugerida e abra o prazo: nasce uma tarefa no quadro, ligada ao caso.",
            "Marque como lida. É isso que tira a linha da fila — o sistema não marca sozinho, porque abrir para olhar não é tratar.",
          ),
          aviso(
            "A data do prazo é sempre sua. O texto costuma anunciar “em cinco dias” e a tela preenche a sugestão, mas dia útil, suspensão e feriado do tribunal não estão escritos na publicação.",
          ),
        ],
      },
      {
        id: "prova",
        titulo: "A certidão, e o arquivo",
        blocos: [
          p(
            "Toda publicação tem certidão oficial em PDF, emitida pelo próprio CNJ: é o documento que se anexa ao processo ou se guarda na pasta do cliente. O botão dela está no pé da folha.",
          ),
          p(
            "O botão CSV baixa o recorte que estiver na tela, com o teor inteiro e o link da certidão de cada linha — para quem confere prazo fora do CRM.",
          ),
          link("/publicacoes", "Abrir Publicações", "A fila do diário, começando pelas não lidas"),
        ],
      },
      {
        id: "o-que-nao-vem",
        titulo: "O que o DJEN não publica",
        blocos: [
          p(
            "Duas coisas ficam fora dele, e é bom saber quais: a pauta de sessão de julgamento da Justiça do Trabalho, que segue só nos cadernos do DEJT, e o edital de intimação de advogados dos tribunais estaduais, que não existe no DJEN em nenhum estado.",
          ),
          p(
            "Também não há fonte pública para a intimação eletrônica lida dentro do sistema do tribunal, que exige o certificado digital do advogado. Enquanto essa fatia importar, o Astrea continua sendo a segunda fonte.",
          ),
        ],
      },
    ],
  },
  {
    id: "tarefas",
    titulo: "Tarefas e agenda",
    resumo: "O quadro do escritório e o eixo do tempo: cinco visões, carga por pessoa e os prazos da semana.",
    icone: "tarefas",
    secoes: [
      {
        id: "quadro",
        titulo: "O quadro",
        blocos: [
          p(
            "Tarefas é um quadro de quatro colunas — a fazer, fazendo, esperando e concluído. Cada cartão tem o caso a que pertence, o responsável e o prazo. Arrastar entre colunas muda o estado e registra quem mudou.",
          ),
          p(
            "As pastilhas de cima trocam a visão: “quadro” e “lista” olham o trabalho por estado e por responsável; “dia”, “semana” e “mês” olham o mesmo dia pelo eixo do tempo — é a agenda, que era uma tela à parte e virou visão daqui.",
          ),
          p(
            "“+ Nova tarefa” pede três coisas: o que fazer, o dia e quem faz — e já vem com hoje, você e o caso aberto. Hora, caso, contato e a coluna em que ela começa ficam em “Mais detalhes”.",
          ),
          imagem("tarefas.png", "O quadro de tarefas com as quatro colunas e a carga por pessoa."),
        ],
      },
      {
        id: "carga",
        titulo: "Carga por pessoa",
        blocos: [
          p(
            "No alto, a carga de cada um: quantas tarefas abertas, quantas atrasadas. O assistente conta junto — trabalho delegado é trabalho, e esconder isso daria uma conta errada do dia.",
          ),
          p("Os recortes (minhas, da equipe, semana, atrasadas) viajam no endereço da página e podem ser mandados por mensagem."),
        ],
      },
      {
        id: "agenda",
        titulo: "A agenda: dia, semana e mês",
        blocos: [
          p(
            "A agenda é leitura, e não cadastro. Ela junta três coisas que já existem: o prazo de uma tarefa, a reunião marcada com o cliente e a mensagem agendada para sair. Cada item diz de onde veio — do CRM ou do Astrea.",
          ),
          imagem("agenda.png", "A semana com prazos, reuniões e mensagens agendadas."),
          aviso(
            "Compromisso se cria onde ele nasce: prazo na tarefa, reunião na reunião, mensagem no agendamento. Um cadastro aqui criaria uma quarta verdade sobre a mesma audiência.",
          ),
        ],
      },
      {
        id: "astrea",
        titulo: "O que vem do Astrea",
        blocos: [
          p(
            "A agenda do Astrea é espelhada por identidade externa, e não por semelhança: varrer dez vezes atualiza a mesma linha em vez de criar dez audiências.",
          ),
          p(
            "Quando os dois lados mexem no mesmo prazo, a mesa não escolhe sozinha: o item vai para uma fila de conflito com os dois valores escritos, e uma pessoa decide. Sobrescrever em silêncio é o pior modo de falhar de uma integração.",
          ),
        ],
      },
      {
        id: "prazo",
        titulo: "Prazo e conclusão",
        blocos: [
          p(
            "Prazo de tarefa aparece também na agenda — é a mesma informação lida de outro ângulo. Concluir uma tarefa tira a carga da pessoa na hora e fecha o alarme do sino.",
          ),
          aviso(
            "Tarefa concluída em massa pelo protocolo também some daqui: quando a peça é protocolada, os prazos daquele caso fecham juntos.",
            "nota",
          ),
        ],
      },
    ],
  },
  {
    id: "rapidas",
    titulo: "Respostas rápidas",
    resumo: "O texto pronto do escritório, com atalho, variáveis e contador de uso.",
    icone: "rapidas",
    secoes: [
      {
        id: "usar",
        titulo: "Usar no meio da conversa",
        blocos: [
          p("Digite “/” no campo de escrever e o atalho da resposta. O texto entra já com as variáveis resolvidas."),
          imagem("rapidas.png", "A lista de respostas prontas por categoria, com o atalho de cada uma."),
        ],
      },
      {
        id: "variaveis",
        titulo: "As variáveis",
        blocos: [
          p(
            "Variável é chave simples entre chaves: {nome}, {processo}, {prazo}. Quem resolve é o servidor, no momento do envio — não a tela.",
          ),
          aviso(
            "Sem valor, a variável vira [a conferir] — nunca vazio. “Olá , tudo bem?” é pior que um marcador visível.",
            "nota",
          ),
        ],
      },
      {
        id: "criar",
        titulo: "Criar e organizar",
        blocos: [
          passos(
            "Em Respostas rápidas, use “Nova resposta”.",
            "Dê um atalho curto (é o que se digita depois da barra) e escolha a categoria — ou crie uma na hora.",
            "Escreva o texto e insira as variáveis pelo chip, que entra na posição do cursor.",
            "Marque se aquela resposta exige aprovação antes de sair, e se o assistente pode usá-la.",
          ),
          p("O contador de uso mostra quais respostas o escritório realmente usa — e quais só ocupam espaço."),
        ],
      },
    ],
  },

  {
    id: "contratos",
    titulo: "Modelos de contrato",
    resumo: "O texto da procuração, da declaração e do contrato de honorários que o cliente assina — um modelo por tese.",
    icone: "contrato",
    secoes: [
      {
        id: "como-a-ia-usa",
        titulo: "Como a IA usa o modelo",
        blocos: [
          p(
            "Quando a tese do caso é confirmada e a ficha fecha, a IA manda o pacote para o cliente assinar. O texto é o do modelo que tem aquela tese marcada; tese sem modelo usa o modelo PADRÃO.",
          ),
          p(
            "Toda variável que o modelo imprime é conferida antes do envio. Se a ficha não tem o endereço, o estado civil ou o e-mail, a IA pergunta ao cliente primeiro — o pacote só sai quando não sobra nenhum [a conferir].",
          ),
        ],
      },
      {
        id: "variaveis",
        titulo: "As variáveis",
        blocos: [
          p(
            "Variável é chave simples entre chaves: {nome}, {cpf}, {estado_civil}, {endereco}, {data}. Os chips inserem na posição do cursor; qualquer outro campo da ficha também vale, pela chave.",
          ),
          aviso(
            "Variável em âmbar é de um campo que a ficha deste escritório não tem: a IA não tem onde guardar a resposta, e o contrato sai com [a conferir]. Crie o campo nos Ajustes.",
            "nota",
          ),
        ],
      },
      {
        id: "importar",
        titulo: "Trazer o modelo da ZapSign",
        blocos: [
          passos(
            "Em Modelos de contrato, use “Importar .docx” e escolha o modelo baixado da ZapSign.",
            "O arquivo é cortado nos três documentos pelo título de cada um, e {{NOME COMPLETO}} vira {nome}.",
            "Confira os três textos, marque as teses que usam o modelo e salve. Importar não grava nada sozinho.",
          ),
          p("“Ver como o cliente lê” mostra os três documentos preenchidos com a ficha de um caso de verdade."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------ escritório */
  {
    id: "modos",
    titulo: "Quanto a IA faz: Sem IA, Copiloto e Piloto",
    resumo: "Uma escolha por setor decide quem faz cada coisa do caso. Os três modos com o exemplo do que muda, os freios que valem sempre e o modo de um caso só.",
    icone: "ia",
    secoes: [
      {
        id: "tres-modos",
        titulo: "Os três modos, com a mesma mensagem chegando",
        blocos: [
          p("O cliente escreve “fui demitido”. O que acontece depende do modo do setor:"),
          tabela(
            ["Modo", "O que acontece"],
            [
              ["Sem IA", "a conversa entra na fila e alguém responde — com / para uma resposta pronta. A ficha, a tese e a conferência são de gente."],
              ["Copiloto", "a resposta já aparece escrita no campo, a ficha se preenche com o que ele contou e a tese vem proposta. Alguém lê e aperta Enter."],
              ["Piloto automático", "a Triagem responde em segundos, qualifica, e com a tese confirmada o contrato sai sozinho. Gente entra quando um freio aciona."],
            ],
          ),
          p(
            "Em todo modo o sistema cria a ficha, cobra documento pela régua, abre a pasta, vigia prazo e dá dono ao caso que travou. Protocolar é sempre do advogado.",
          ),
          link("/ajustes", "Escolher o modo", "o cartão “Quanto a IA faz”, em Ajustes"),
        ],
      },
      {
        id: "freios",
        titulo: "Os freios que valem sempre",
        blocos: [
          itens(
            "O cliente pediu uma pessoa: a IA sai do caso na hora e a tarefa nasce para quem cuida dele.",
            "A IA não tem certeza (valor, documento ilegível, tese ambígua): o caso vai para uma pessoa, com o motivo escrito.",
            "Três lembretes sem resposta: não existe o quarto — o caso volta para uma pessoa.",
            "Fora do horário de atendimento a IA não puxa conversa.",
            "Sem motor de IA, o modo desce um degrau: o Piloto vira Copiloto e o Copiloto vira Sem IA. Nada para.",
          ),
          p("Os freios não têm botão: são regra fixa, iguais para todo setor."),
        ],
      },
      {
        id: "um-caso",
        titulo: "O modo de um caso só",
        blocos: [
          p(
            "Um caso pode seguir outro modo que o do setor — o cliente difícil que você prefere atender à mão, ou o caso simples que pode ir no piloto. Pôr um caso no piloto é o que antes se chamava delegar; retomar o atendimento tira o caso do piloto.",
          ),
          aviso("Ligar o piloto, no setor ou num caso, pede confirmação: nele sai mensagem para o cliente sem ninguém aprovar.", "nota"),
        ],
      },
      {
        id: "fila",
        titulo: "A fila de aprovação",
        blocos: [
          p(
            "O que sai do CRM e não é do modo — cadastro no Astrea, mensagem que um agente preparou fora do Piloto — espera numa fila até alguém aprovar. Cada item mostra o que será feito e com quais dados; nada acontece antes do toque.",
          ),
        ],
      },
    ],
  },
  {
    id: "reunioes",
    titulo: "Reuniões",
    resumo: "Gravar o alinhamento, transcrever, separar quem falou e transformar o combinado em fatos.",
    icone: "calls",
    secoes: [
      {
        id: "gravar",
        titulo: "Gravar e transcrever",
        blocos: [
          p(
            "A reunião de alinhamento é gravada pela própria mesa. Terminada, ela é transcrita e os falantes são separados — cliente e escritório —, com a hora de cada trecho.",
          ),
          imagem("reunioes.png", "Uma gravação com a transcrição ao lado, por falante."),
        ],
      },
      {
        id: "virar-fato",
        titulo: "O que a reunião decidiu",
        blocos: [
          p(
            "A mesa extrai da transcrição o que foi combinado e propõe como fatos e pendências do caso. O trecho citável vira depoimento na seção de fatos da petição — o mesmo dado que se marca na conversa.",
          ),
        ],
      },
    ],
  },
  {
    id: "acervo",
    titulo: "Documentos (acervo)",
    resumo: "A pasta de cada cliente, o que já foi protocolado e as peças que servem de modelo.",
    icone: "pasta",
    secoes: [
      {
        id: "pastas",
        titulo: "A pasta do cliente",
        blocos: [
          p(
            "Tudo que chega de um cliente — foto, PDF, áudio — é guardado na pasta dele, com o nome do documento que a classificação deu. A tela de Documentos mostra essas pastas, com busca por arquivo e por cliente.",
          ),
          imagem("documentos.png", "O acervo com as pastas dos clientes e a busca."),
        ],
      },
      {
        id: "modelos",
        titulo: "As peças que a casa já protocolou",
        blocos: [
          p(
            "As iniciais protocoladas pelo escritório são o acervo que a IA usa como forma ao montar uma peça nova. Não é modelo genérico: é o jeito desta casa escrever, aplicado ao caso de agora.",
          ),
        ],
      },
    ],
  },
  {
    id: "contatos",
    titulo: "Contatos (a carteira)",
    resumo:
      "A base inteira do escritório, recortada pela pergunta do dia: quem está parado, quem ninguém pegou, quem falta documento.",
    icone: "contatos",
    secoes: [
      {
        id: "o-que-e",
        titulo: "Não é o catálogo do celular",
        blocos: [
          p(
            "A carteira é a base INTEIRA do escritório — todo mundo que já falou com a casa, e não só quem tem conversa aberta na mesa. A mesa responde 'o que faço agora'; a carteira responde 'quem eu deveria estar chamando'.",
          ),
          p(
            "O catálogo de contatos do aparelho de WhatsApp morreu junto com a caixa de entrada dele: era uma segunda lista ao lado da mesa, e quem atende trabalha na mesa. Isto aqui é outra coisa.",
          ),
        ],
      },
      {
        id: "recorte",
        titulo: "O recorte é a pergunta",
        blocos: [
          p(
            "Etapa, responsável, origem, tese e período: cada pastilha corta a carteira, e as pastilhas se somam. É assim que se pergunta 'quem está parado em aguardando documentos com a Camila desde o mês passado' sem abrir ficha nenhuma.",
          ),
          p(
            "O recorte mora no ENDEREÇO. Isso quer dizer que ele se manda por mensagem: monte a pergunta, copie a barra do navegador e mande para o colega — ele abre exatamente a mesma lista. O botão voltar desfaz uma pastilha por vez.",
          ),
          aviso(
            "Os números do painel contam o BANCO, não a página que está na tela. Se a lista mostra 200 e o painel diz 528, os dois estão certos: um é o que caberia rolar, o outro é o que existe.",
            "nota",
          ),
        ],
      },
      {
        id: "visoes",
        titulo: "Painel, lista e mapa",
        blocos: [
          p(
            "O mesmo recorte, visto de três jeitos: o PAINEL resume em números e gráficos (é onde se vê que uma etapa está engasgando), a LISTA é a tabela para varrer nome por nome, e o MAPA cruza etapa e responsável para achar a célula onde o trabalho empoçou.",
          ),
          p("Tocar num contato abre a ficha dele por cima, com a conversa a um clique. Fechar volta para o mesmo recorte."),
        ],
      },
    ],
  },
  {
    id: "criativos",
    titulo: "Criativos (de qual anúncio o lead veio)",
    resumo:
      "O código do anúncio na frase pronta (AD04) vira o criativo da ficha sozinho — e a tela conta quantos leads cada criativo trouxe e quantos fecharam.",
    icone: "criativos",
    secoes: [
      {
        id: "como-marca",
        titulo: "Como o lead chega marcado",
        blocos: [
          p(
            "O gestor de tráfego põe um CÓDIGO na frase pronta que o cliente manda pelo anúncio — por exemplo “Olá, vi o anúncio AD04”. Quando a mensagem chega, o sistema procura os códigos cadastrados e grava o nome do criativo (e a campanha, se o cadastro tiver) na ficha do lead.",
          ),
          itens(
            "Vale nas três primeiras mensagens do cliente, enquanto o criativo da ficha estiver vazio: o “oi” antes da frase não atrapalha, e um “AD04” no meio da conversa semanas depois não muda nada.",
            "Maiúscula ou minúscula tanto faz, mas o código tem de estar sozinho: “ad04” casa, “CAD045” não.",
            "Nunca troca o que alguém escreveu na ficha. A trilha do caso diz “Criativo pelo código AD04”, com o autor “sistema (código do anúncio)” — quem abrir o caso sabe que não foi gente.",
            "Quando o anúncio da Meta manda os dados dele junto, o código cadastrado vence o texto do anúncio: é o nome que o escritório escolheu e o que cruza com o gasto. O identificador do anúncio continua guardado.",
          ),
        ],
      },
      {
        id: "cadastrar",
        titulo: "Cadastrar um código",
        blocos: [
          passos(
            "Em Criativos, toque em “Cadastrar código”.",
            "Escreva o código como ele vem na mensagem (AD04) e o nome do criativo (“Criativo Felipe no escritório”). A campanha é opcional.",
            "Grave. Os leads que JÁ chegaram com aquele código e estão sem criativo são marcados na hora — a tela diz quantos.",
          ),
          p(
            "Não precisa esperar o gestor avisar: quando chega uma mensagem com cara de código (letras maiúsculas e número) que ninguém cadastrou, ela aparece no alto da tela em “códigos novos”, com o trecho da mensagem. “Cadastrar” abre a caixa já com o código; “Não é código” tira da lista para sempre.",
          ),
          aviso(
            "Escreva o nome do criativo IGUAL ao nome do anúncio no gerenciador da Meta: é por ele que o gasto sincronizado se junta aos leads, e o custo por lead e por contrato aparecem sozinhos.",
            "nota",
          ),
          p(
            "Desligar um código (o interruptor na lista) faz ele parar de marcar os próximos leads; quem já foi marcado continua. Renomear também vale daqui para frente.",
          ),
        ],
      },
      {
        id: "numeros",
        titulo: "Os números do período",
        blocos: [
          p(
            "Escolha o período no alto (hoje, 7 dias, 30 dias, o mês, ou um intervalo). A tabela mostra, para cada criativo, quantos leads CHEGARAM no período e, desses, quantos já fecharam — a conversão é sempre dos leads daquele período, e o dia é o do fuso do escritório.",
          ),
          p(
            "“Fechou” é o caso com a data da assinatura preenchida (o sistema grava quando o contrato volta assinado) ou que chegou a uma etapa marcada como ganho no funil. Toque no título de uma coluna para ordenar; toque no nome do criativo para abrir a mesa só com os leads dele.",
          ),
          p(
            "Criativo com gasto e nenhum lead aparece com zero — é o dinheiro indo embora. “Sem criativo” são os leads que chegaram sem anúncio e sem código.",
          ),
        ],
      },
    ],
  },
  {
    id: "assistentes",
    titulo: "Assistentes da mesa",
    resumo: "Conversar com um assistente que consulta e mexe no CRM por você — e por que ele às vezes aparece indisponível.",
    icone: "ia",
    secoes: [
      {
        id: "o-que-e",
        titulo: "Uma conversa, não um campo de busca",
        blocos: [
          p(
            "Assistentes é a porta para conversar com um assistente que trabalha dentro do CRM: ele consulta a mesa, lê a ficha, procura no acervo e escreve. Cada conversa guarda um assunto, e o assistente é escolhido na hora de criar.",
          ),
          p(
            "É diferente da leitura do caso, na coluna do atendimento: ela lê UM atendimento e sugere a resposta dele. O assistente daqui é um colega para quem se pergunta qualquer coisa do escritório, sem um caso aberto.",
          ),
          link("/agentes", "Abrir Assistentes", "A tela, com as conversas de todo mundo"),
        ],
      },
      {
        id: "indisponivel",
        titulo: "Por que um assistente aparece indisponível",
        blocos: [
          p(
            "Só assistente DISPONÍVEL abre conversa, e o motivo fica escrito ao lado do nome. São três motivos, e cada um tem um dono:",
          ),
          itens(
            "Assistente na máquina de alguém precisa daquele computador conectado — quem liga é a pessoa naquele computador, na IA do atendimento → Motor.",
            "Assistente instalado no escritório precisa estar ativo na IA do atendimento → Motor — quem liga é quem cuida da instalação.",
            "O assistente do escritório precisa de chave de IA gravada — quem grava é um sócio, na IA do atendimento → Motor.",
          ),
          p(
            "Assistente sem conversa aberta não é defeito: é a tela dizendo quem está de pé antes de você digitar uma pergunta que não teria resposta.",
          ),
        ],
      },
      {
        id: "autorizar",
        titulo: "Quando ele pede autorização",
        blocos: [
          p(
            "Ler é livre; ESCREVER no CRM pede autorização na tela, antes de rodar. O pedido aparece como um cartão na própria conversa, dizendo o que ele quer fazer, com os botões Permitir e Recusar: um toque responde, e nada acontece até alguém responder.",
          ),
          aviso(
            "'Permitir sempre' não se desfaz por esta tela — por isso ele pergunta de novo antes de gravar essa escolha. Libere caso por caso enquanto estiver conhecendo o assistente.",
            "atencao",
          ),
          p(
            "Tudo que ele fez fica na transcrição da conversa, com o nome da ferramenta em português. Ferramenta que aparece como 'Trabalhando' é ferramenta nova que ainda não ganhou frase — avise quem cuida do sistema.",
          ),
        ],
      },
    ],
  },
  {
    id: "agentes-ia",
    titulo: "IA do atendimento",
    resumo: "Sete agentes, um por momento do caso. Cada um tem dois campos: quando age e o que faz.",
    icone: "agenteIa",
    secoes: [
      {
        id: "sete-agentes",
        titulo: "Sete agentes, prontos no primeiro dia",
        blocos: [
          p(
            "Triagem, Qualificação, Contrato, Documentos, Pós-reunião, Petição e Andamento. Cada um cuida de um momento do caso e já vem escrito a partir do manual do escritório e das conversas reais: primeiro nome, uma pergunta por vez, sem juridiquês, sem prometer valor.",
          ),
          p(
            "Quem nunca abrir esta tela tem um escritório funcionando. O cartão diz \"padrão\" enquanto ninguém mexeu — e o padrão melhora a cada versão do sistema.",
          ),
          link("/agentes-ia", "Abrir a IA do atendimento", "Os sete cartões"),
        ],
      },
      {
        id: "dois-campos",
        titulo: "Quando age e o que faz",
        blocos: [
          p(
            "QUANDO AGE são fichas: chegou um contato, o contrato saiu, o cliente mandou um documento, a reunião foi transcrita, o processo foi protocolado. Toque para ligar ou desligar; sem nenhuma ficha o agente fica calado.",
          ),
          p(
            "O QUE FAZ é o texto que o agente segue. Escreva como falaria com alguém novo no escritório — a conversa, a ficha e os documentos da tese já entram sozinhos. Mexeu, o cartão passa a dizer \"editado por\" e quando; \"voltar ao padrão\" desfaz em um toque.",
          ),
          p(
            "Quanto a IA faz sozinha não é do agente: é o modo do escritório, em Ajustes. No Copiloto o agente escreve e alguém envia; no Piloto ele envia; sem IA, ninguém escreve por ela.",
          ),
        ],
      },
      {
        id: "ensaiar",
        titulo: "Ensaiar com um caso",
        blocos: [
          p(
            "Dentro do agente, \"Ensaiar com um caso\" escolhe um caso da mesa e mostra, passo a passo, o que aconteceria e o texto que o agente escreveria. Nada é enviado.",
          ),
        ],
      },
      {
        id: "o-que-ela-sabe",
        titulo: "O que ela sabe",
        blocos: [
          p(
            "O objetivo de cada etapa vai no pedido quando a IA escreve a próxima mensagem. Os documentos de cada tese são o checklist e o que a cobrança pede. Os textos da cobrança são as mensagens de D+2, D+5 e D+10, com {nome}, {faltantes} e {dias}.",
          ),
          link("/agentes-ia?secao=conhecimento", "Abrir o que ela sabe", "Etapas, documentos por tese e cobrança"),
        ],
      },
    ],
  },
  {
    id: "equipe",
    titulo: "Equipe e acessos",
    resumo: "Quem entra na mesa, com qual papel, e o que cada papel pode fazer.",
    icone: "equipe",
    secoes: [
      {
        id: "pessoas",
        titulo: "Pessoas e papéis",
        blocos: [
          passos(
            "Em Equipe e acessos, use “Convidar pessoa”.",
            "Informe nome, e-mail e o papel dela.",
            "A senha provisória aparece uma vez só — copie e entregue por um canal seguro.",
            "Na primeira entrada, a pessoa troca a senha — a mesa só abre depois disso — e cai nos Primeiros passos do papel dela.",
          ),
          imagem("equipe.png", "A lista da equipe com papel e último acesso."),
        ],
      },
      {
        id: "permissoes",
        titulo: "Permissões são ações",
        blocos: [
          p(
            "Um papel é um conjunto de ações: ver leads, escrever na esteira, aprovar documento, mandar mensagem, mexer em configuração. A tela agrupa as ações pelas telas da mesa, para ficar legível.",
          ),
          aviso(
            "Tirar uma ação de um papel tira o lugar do trilho de quem tem esse papel. Porta que não abre não é porta — a mesa prefere não mostrar.",
            "nota",
          ),
        ],
      },
    ],
  },
  {
    id: "ajustes",
    titulo: "Ajustes",
    resumo: "Quatro cartões: quanto a IA faz, o horário, os números do setor e quem é quem.",
    icone: "settings",
    secoes: [
      {
        id: "modo",
        titulo: "Quanto a IA faz",
        blocos: [
          p(
            "Três escolhas, uma por vez: Sem IA (o sistema anda por regra fixa), Copiloto (a IA prepara, a pessoa confirma com um toque) e Piloto automático (os assistentes conduzem o caso e a pessoa entra quando um freio aciona, e para protocolar).",
          ),
          aviso(
            "Ligar o piloto pede confirmação: nele sai mensagem para o cliente sem aprovação. Quem não cuida das configurações vê o cartão só para leitura.",
            "nota",
          ),
        ],
      },
      {
        id: "horario",
        titulo: "Horário de atendimento",
        blocos: [
          p(
            "A semana desenhada com a faixa das horas em que régua, plantão e IA podem mandar mensagem. Vale todos os dias; o fim precisa vir depois do início.",
          ),
        ],
      },
      {
        id: "numeros",
        titulo: "Números do setor",
        blocos: [
          p(
            'Diz se o WhatsApp está conectado e por qual provedor. "Conectar ou parear" abre a folha com o QR e o teste de envio; "Abrir setores" leva ao número de cada setor.',
          ),
        ],
      },
      {
        id: "equipe",
        titulo: "Quem é quem",
        blocos: [
          p(
            "A equipe com o papel de cada um — é pelo papel que a régua cobra e a IA devolve. Ali também fica a lista de advogados vigiados nos diários oficiais.",
          ),
          p("Servidor, integrações e credenciais ficam em Sistema, para quem cuida das configurações."),
        ],
      },
    ],
  },
  {
    id: "avisos",
    titulo: "Notificações",
    resumo: "O sino, o que ele conta e por que a conta dele é do servidor.",
    icone: "sino",
    secoes: [
      {
        id: "sino",
        titulo: "O sino",
        blocos: [
          p(
            "O sino junta o que precisa de você: mensagem não lida, prazo vencendo, documento esperando conferência, item na fila de automação. Clicar leva direto ao lugar do problema.",
          ),
          imagem("notificacoes.png", "O balão do sino com as pendências agrupadas."),
          p("Notificações também é lugar com endereço próprio: /notificacoes abre a lista inteira, útil no celular."),
          p(
            "A lista se refaz sozinha a cada movimento na mesa — não há botão de atualizar, e o que está na tela é o retrato de agora.",
          ),
        ],
      },
    ],
  },

  /* ---------------------------------------------------- teclado e apuros */
  {
    id: "atalhos",
    titulo: "Atalhos de teclado",
    resumo: "O que dá para fazer sem tirar a mão do teclado.",
    icone: "certo",
    secoes: [
      {
        id: "gerais",
        titulo: "Em qualquer tela",
        blocos: [
          atalhos(
            ["Ctrl+K / Cmd+K", "abre a paleta: área, cliente, caso — ou uma ação, como trocar de setor"],
            ["Esc", "fecha a caixa, o painel ou a busca aberta"],
            ["/", "no campo de escrever, abre as respostas rápidas"],
          ),
        ],
      },
      {
        id: "conversa",
        titulo: "Na conversa",
        blocos: [
          atalhos(["Enter", "envia"], ["Shift+Enter", "quebra a linha"], ["↑ ↓", "anda pelos resultados da busca da conversa"]),
        ],
      },
      {
        id: "conferencia",
        titulo: "Na conferência de documentos",
        blocos: [atalhos(["1", "aprova"], ["2", "ilegível"], ["3", "dispensa"], ["← →", "anda entre os documentos"])],
      },
    ],
  },
  {
    id: "apuros",
    titulo: "Quando algo dá errado",
    resumo: "Os apuros comuns do dia e o que fazer em cada um.",
    icone: "risco",
    secoes: [
      {
        id: "mensagem-nao-saiu",
        titulo: "A mensagem não chegou ao cliente",
        blocos: [
          p(
            "A mesa avisa na hora, embaixo da bolha: “não chegou ao cliente”. Isso quer dizer que o WhatsApp do escritório está fora do ar ou sem credencial — a mensagem está gravada, mas não saiu.",
          ),
          passos("Confira em Ajustes se o provedor está de pé.", "Resolvido, use “Enviar agora” na própria bolha."),
        ],
      },
      {
        id: "documento-nao-abre",
        titulo: "O documento não abre na conferência",
        blocos: [
          p(
            "Se a conferência diz que o arquivo não está na pasta, não aprove: conferir sem ver o documento é assinar embaixo do que não se leu. Peça o reenvio ao cliente marcando o item como ilegível.",
          ),
        ],
      },
      {
        id: "minuta-parou",
        titulo: "A minuta parou no meio",
        blocos: [
          p(
            "Montar uma inicial leva de um a dois minutos. Sair da tela cancela o pedido: o servidor vê o corte e não grava nada. Se ela parar mesmo, a tela diz o motivo em português — em geral falta papel timbrado do escritório ou o assistente de IA não está de pé.",
          ),
          link("/ajustes", "Ajustes", "onde se confere o motor de IA e o timbrado do escritório"),
        ],
      },
      {
        id: "prazo-duplicado",
        titulo: "Um prazo apareceu duas vezes",
        blocos: [
          p(
            "Prazo espelhado do Astrea é casado por identidade externa, então duplicar não deveria acontecer. Se acontecer, é sinal de que a mesma audiência foi criada nos dois lados: resolva pela fila de conflito, escolhendo qual valor vale.",
          ),
        ],
      },
      {
        id: "nao-vejo",
        titulo: "Não encontro uma tela que existia",
        blocos: [
          p(
            "Provavelmente é permissão: o trilho só mostra o que o seu papel alcança. Peça a quem administra o escritório para conferir o seu papel em Equipe e acessos.",
          ),
        ],
      },
    ],
  },

  /* --------------------------------------------------- o que entra na mesa */
  {
    id: "integracoes",
    titulo: "Como as mensagens entram e saem",
    resumo:
      "O caminho de uma mensagem, de ponta a ponta: o aviso que chega, o canal, a entrega, o ensaio e o que fazer quando trava.",
    icone: "caixa",
    secoes: [
      {
        id: "entrada",
        titulo: "Por onde a mensagem entra",
        blocos: [
          p(
            "O cliente escreve no WhatsApp (ou responde um anúncio no Instagram). O provedor avisa a mesa na hora, e é esse aviso que cria o atendimento — ninguém cadastra ninguém à mão.",
          ),
          passos(
            "O provedor entrega o evento na porta do CRM, autenticado por segredo.",
            "A mesa acha o cliente pelo telefone; se não existir, cria o caso e guarda a campanha e o criativo do anúncio.",
            "Se veio arquivo, a mesa baixa a mídia, guarda na pasta do cliente e classifica contra o checklist.",
            "Se veio áudio, transcreve e põe o texto embaixo da bolha.",
            "A conversa aberta recebe o evento na hora, sem recarregar.",
          ),
          aviso(
            "A mesa responde “recebido” ao provedor mesmo quando algo dá errado no meio, de propósito: parceiro que recebe erro repete o evento por horas. O que falhou fica escrito na conversa ou na trilha — nunca sumido.",
            "nota",
          ),
        ],
      },
      {
        id: "saida",
        titulo: "Por onde a mensagem sai — e os quatro estados dela",
        blocos: [
          tabela(
            ["Estado", "O que aconteceu"],
            [
              ["Um tique", "saiu do CRM e o provedor aceitou"],
              ["Dois tiques", "chegou no aparelho do cliente"],
              ["Dois tiques azuis", "o cliente leu"],
              ["Na fila", "a mensagem existe no CRM esperando alguém aprovar em Automação"],
              ["Ensaio", "gravada, mas NÃO enviada — o provedor está fora do ar ou sem credencial"],
              ["Falhou", "o provedor recusou; a bolha fica vermelha e dá para reenviar"],
            ],
          ),
          p(
            "Mensagem em ensaio não é mensagem perdida: ela fica na conversa, marcada, e o botão “Enviar agora” a manda assim que o canal voltar.",
          ),
        ],
      },
      {
        id: "quem-fala",
        titulo: "Quem falou: pessoa, agente ou sistema",
        blocos: [
          p(
            "Toda escrita na mesa guarda AUTOR. A bolha diz quem foi: o nome de quem atendeu, “IA · copiloto” quando foi o assistente, ou o serviço que gerou o registro (o WhatsApp, a assinatura, o agente do Astrea).",
          ),
          p(
            "Isso não é enfeite: quando alguém pergunta “quem prometeu isso ao cliente?”, a resposta tem de estar na tela, e não na memória de quem estava lá.",
          ),
        ],
      },
    ],
  },
  {
    id: "celular",
    titulo: "No celular",
    resumo: "A mesma mesa numa tela de bolso: o que muda, o que continua igual e o que é melhor deixar para o computador.",
    icone: "whatsapp",
    secoes: [
      {
        id: "o-que-muda",
        titulo: "O que muda",
        blocos: [
          itens(
            "O trilho vira uma barra embaixo, com o trabalho do dia; o resto fica atrás de “Mais”.",
            "A lista é a tela inicial; tocar num caso abre a conversa por cima.",
            "O painel do caso vira uma gaveta que sobe pelo botão do cabeçalho.",
            "A conferência de documentos ocupa a tela inteira, uma decisão por vez.",
          ),
          p("Endereço é o mesmo: um link mandado do computador abre no lugar certo no telefone."),
        ],
      },
      {
        id: "melhor-no-computador",
        titulo: "O que é melhor no computador",
        blocos: [
          p(
            "Editar a petição. O editor é o .docx de verdade, com margens e paginação: dá para ler e revisar no telefone, mas escrever uma inicial numa tela de bolso é trabalho contra a ferramenta.",
          ),
        ],
      },
    ],
  },
  {
    id: "registro",
    titulo: "Registro, LGPD e auditoria",
    resumo: "Por que a mesa guarda tudo, o que fica registrado e como isso protege o escritório e o cliente.",
    icone: "cadeado",
    secoes: [
      {
        id: "trilha",
        titulo: "A trilha de tudo",
        blocos: [
          p(
            "Toda escrita guarda quem fez, quando e o que mudou: mensagem enviada, etapa movida, documento aprovado, campo da ficha alterado, peça salva, caso delegado à IA.",
          ),
          p(
            "A conversa tem valor probatório, e por isso o desenho separa o que foi DITO do que o sistema FEZ: a bolha é fala, o marco é ato. Misturar os dois criaria prova que não existe.",
          ),
        ],
      },
      {
        id: "dados-do-cliente",
        titulo: "Dado do cliente",
        blocos: [
          itens(
            "Documento enviado pelo cliente fica na pasta dele, dentro do servidor do escritório.",
            "Mensagem apagada pelo cliente continua na trilha marcada como apagada — o conteúdo sai, o registro de que houve algo fica.",
            "O rodapé da conversa lembra, todo dia, que aquilo é registrado para fins probatórios.",
          ),
          aviso(
            "Quem tem acesso a quê é decidido em Equipe e acessos. Papel com menos ações vê menos — inclusive menos dado de cliente.",
            "nota",
          ),
        ],
      },
    ],
  },
  {
    id: "copiloto-detalhe",
    titulo: "A IA, por dentro",
    resumo: "O que a IA faz, o que ela nunca faz sozinha, e como ler o que ela responde.",
    icone: "ia",
    secoes: [
      {
        id: "o-que-faz",
        titulo: "Onde a IA trabalha na mesa",
        blocos: [
          tabela(
            ["Lugar", "O que ela faz"],
            [
              ["Painel do caso", "resume o caso, aponta risco e sugere a próxima ação"],
              ["Conversa", "propõe resposta ao cliente, que alguém lê antes de mandar"],
              ["Ficha", "propõe o preenchimento a partir do que foi conversado"],
              ["Anexo recebido", "classifica o documento contra o checklist da tese"],
              ["Petição", "extrai os fatos da conversa e monta a minuta sobre o acervo da casa"],
              ["Reunião", "transcreve, separa os falantes e tira o que foi combinado"],
            ],
          ),
        ],
      },
      {
        id: "o-que-nao-faz",
        titulo: "O que ela nunca faz sozinha",
        blocos: [
          itens(
            "Falar com o cliente sem aprovação — no Copiloto alguém confirma; só o Piloto automático manda sozinho, e para quando um freio aciona.",
            "Gravar fora do CRM (Astrea, protocolo) sem passar pela fila.",
            "Inventar prazo, documento ou valor: o que é fato do escritório sai do banco.",
            "Continuar num atendimento onde o cliente pediu uma pessoa.",
          ),
        ],
      },
      {
        id: "ler-a-resposta",
        titulo: "Como ler o que ela responde",
        blocos: [
          p(
            "Toda leitura do assistente mostra a hora e diz quando nada mudou desde então. Se o caso andou, “Reler” refaz — resposta velha com cara de nova é o pior tipo de ajuda.",
          ),
          p(
            "Na petição, cada fato traz a procedência: “msg 14:12” quando saiu da conversa, e a marca do assistente quando é conclusão jurídica. O que vai para a peça é o que alguém marcou.",
          ),
        ],
      },
    ],
  },
  {
    id: "operacao",
    titulo: "Operação do escritório",
    resumo: "Backup, o que roda sozinho e o que fazer antes de mexer em algo delicado.",
    icone: "settings",
    secoes: [
      {
        id: "backup",
        titulo: "Backup",
        blocos: [
          p(
            "O backup guarda o banco, os documentos e a configuração. Ele roda no servidor do escritório — e a cópia sai da máquina, porque backup que mora no mesmo disco não é backup.",
          ),
          aviso(
            "Antes de qualquer mudança grande (importar planilha, trocar as etapas, migrar servidor): rode um backup na hora.",
          ),
        ],
      },
      {
        id: "o-que-roda-sozinho",
        titulo: "O que roda sozinho",
        blocos: [
          itens(
            "A régua de cobrança, dentro do horário comercial do escritório.",
            "A varredura da agenda do Astrea, que espelha prazos e audiências.",
            "Os agentes, cada um no seu gatilho — conforme o modo do setor.",
            "A fila de aprovação, quando alguém aprova um item.",
          ),
          p(
            "Quanto disso fala com o cliente sem aprovação é o modo do setor: Sem IA, Copiloto ou Piloto (ver “Quanto a IA faz”).",
          ),
        ],
      },
    ],
  },
  {
    id: "astrea",
    titulo: "Astrea",
    resumo: "Como saber se o Astrea está respondendo ao CRM, quem resolve quando não está, e o que a agenda de lá tem em aberto.",
    icone: "astrea",
    secoes: [
      {
        id: "o-que-e-esta-tela",
        titulo: "Para que serve esta tela",
        blocos: [
          p(
            "O Astrea é o sistema jurídico do escritório, e a agenda dele é a que vale para o fórum. O CRM fala com ele por trás: espelha prazos e audiências, abre o caso quando o contrato é assinado, consulta a agenda. Quando essa conversa para, o sintoma aparece longe da causa — o prazo não chega, o caso não abre — e a frase que circula é “o Astrea caiu”.",
          ),
          p(
            "Esta tela responde a essa frase em uma linha, e diz de quem é o problema. Ela não muda nada: nem no Astrea, nem no CRM. Conferir de novo é sempre seguro, quantas vezes quiser.",
          ),
        ],
      },
      {
        id: "a-corrente",
        titulo: "A corrente, elo a elo",
        blocos: [
          p(
            "A conferência não é um sim/não, porque as quedas possíveis pedem pessoas diferentes. Ela é uma corrente: o primeiro elo vermelho explica a queda, e os de baixo aparecem como “não conferido” — não como falha, para a tela não apontar dois culpados.",
          ),
          tabela(
            ["Elo que arrebentou", "Quem resolve"],
            [
              [
                "A ponte com o Astrea neste servidor",
                "Quem cuida do servidor: falta ligar a integração no arquivo de configuração.",
              ],
              ["Credencial do escritório", "Quem cuida do servidor: falta gravar o login e a senha do Astrea."],
              [
                "Sessão no Astrea",
                "O advogado, na tela do Astrea: normalmente é senha trocada ou conta pedindo código em dois passos — o servidor não entra sozinho nesse caso.",
              ],
              ["Painel da agenda", "Ninguém: é outro servidor deles, e o Astrea segue servindo. Falta só a contagem."],
              [
                "Nenhum, e ainda assim não responde",
                "Ninguém: é queda do lado deles. A saída é esperar, e não mexer na configuração.",
              ],
            ],
          ),
          aviso(
            "“O Astrea responde, mas a agenda não” não é o Astrea fora do ar: a tela dele continua abrindo, e o que falta é a contagem de prazos.",
          ),
        ],
      },
      {
        id: "os-prazos",
        titulo: "Os prazos em aberto",
        blocos: [
          p(
            "Os quatro cartões vêm do próprio Astrea — não do nosso banco —, e é por isso que eles também servem de prova de que a integração está viva: número na tela é resposta com conteúdo, e não só um “200 OK”.",
          ),
          itens(
            "O número grande é o de PRAZO, porque prazo é o que perde caso.",
            "A barra colorida mostra a composição do recorte: prazo, audiência, tarefa e compromisso.",
            "“Atrasado” é o único cartão que acende, e só quando há prazo vencido em aberto.",
          ),
          p(
            "Prazo do fórum se trata no Astrea — o botão “Abrir o Astrea” leva à tela de lá pela nossa origem. Tarefa do escritório se trata em Tarefas e agenda, que é onde o prazo espelhado vira trabalho de alguém.",
          ),
          link("/tarefas?visao=semana", "Tarefas e agenda", "onde o prazo espelhado do Astrea vira trabalho com responsável"),
        ],
      },
    ],
  },
  {
    id: "setores",
    titulo: "Setores",
    resumo:
      "Trabalhista, Previdenciário e o que vier: cada setor é um escritório completo, com números, equipe e funil próprios.",
    icone: "matriz",
    secoes: [
      {
        id: "o-que-e-um-setor",
        titulo: "O que é um setor",
        blocos: [
          p(
            "Cada setor tem o seu banco: os clientes, as conversas, o funil, as teses, os agentes e os números de WhatsApp de um não aparecem no outro. A pessoa entra uma vez só e troca de setor no nome ao lado da marca — e a cor desse nome é a do setor, para ninguém gravar no lugar errado sem perceber.",
          ),
          p(
            "Esta tela é do master e do dev. Ela mostra os setores lado a lado: o modo da IA de cada um, os números e o estado de cada número, quem trabalha ali e o que o mês rendeu (leads, contratos e protocolos).",
          ),
        ],
      },
      {
        id: "trocar",
        titulo: "Trocar de setor",
        blocos: [
          passos(
            "Toque no nome do setor, ao lado da marca — ou abra a paleta e digite o nome do outro setor.",
            "Escolha o setor: só aparecem os que a sua conta alcança.",
            "A mesa recarrega inteira no setor novo. Os endereços continuam valendo, mas apontam para os casos de lá.",
          ),
          aviso("Quem alcança um setor só não vê a troca — e é isso mesmo. Os primeiros passos são por setor: num setor novo, a lista recomeça.", "nota"),
        ],
      },
      {
        id: "numeros",
        titulo: "Vários números por setor",
        blocos: [
          p(
            "Um setor pode ter mais de um WhatsApp — “comercial” e “documentos”, por exemplo. Cada número tem o seu pareamento: “+ número” cadastra, “Parear QR” abre o QR para ler no celular daquele número, e o rótulo se troca tocando nele.",
          ),
          itens(
            "Cada conversa responde pelo número por onde o cliente escreveu. Quem mandou mensagem para “documentos” recebe a resposta de “documentos”.",
            "Quem ainda não falou com o setor recebe pelo número PADRÃO. “Tornar padrão” troca qual é.",
            "Tirar um número apaga o pareamento dele; os casos que falavam por ele passam a sair pelo padrão. O último número não sai.",
          ),
        ],
      },
      {
        id: "novo-setor",
        titulo: "Setor novo, copiado de outro",
        blocos: [
          p(
            "“+ Novo setor” cria o banco e, se escolher de onde copiar, traz a configuração daquele setor: etapas, campos, listas (teses e documentos), modelos de contrato, agentes e respostas prontas. Cliente nenhum vem junto — o setor novo começa vazio, com o funil do outro.",
          ),
          aviso("A chave do setor é o nome da pasta do banco e não muda depois. O nome e a cor mudam quando quiser."),
        ],
      },
      {
        id: "encaminhar",
        titulo: "Encaminhar um cliente a outro setor",
        blocos: [
          p(
            "O trabalhista que também tem caso de INSS: encaminhar abre o caso dele no outro setor, com o nome, o telefone e os campos da ficha que existem lá. Os dois lados ganham uma nota na trilha dizendo de onde veio e para onde foi. Se o telefone já é caso no destino, nada se duplica: a nota vai para o caso que existe.",
          ),
        ],
      },
    ],
  },
  {
    id: "sistema",
    titulo: "Sistema",
    resumo: "A máquina por trás da mesa: saúde do servidor, motor de IA e trilha de acesso. Só o dev vê.",
    icone: "painel",
    secoes: [
      {
        id: "para-que-serve",
        titulo: "Para que serve esta tela",
        blocos: [
          p(
            "É onde mora o que é de quem cuida do servidor, e não de quem atende: memória do processo, as rotas mais lentas, a origem autorizada a gravar, qual motor de IA e qual modelo estão valendo, e a trilha de quem entrou e do que mudou nos acessos.",
          ),
          p(
            "Nada aqui se muda pela tela. O que está no arquivo de configuração do servidor exige acesso à máquina e reinício — um botão aqui seria um interruptor mentindo. “O Astrea caiu?” leva à conferência da integração com o Astrea, e “Observabilidade” ao painel de erros e telemetria.",
          ),
        ],
      },
    ],
  },
  {
    id: "observabilidade",
    titulo: "Observabilidade",
    resumo: "Tudo o que o sistema faz, o que quebrou e quem fez o quê. Só master e dev.",
    icone: "painel",
    secoes: [
      {
        id: "erros",
        titulo: "Erros",
        blocos: [
          p(
            "Cada falha — do servidor, de um provedor (WhatsApp, Astrea, diário oficial), da IA ou da tela de alguém — vira uma linha, agrupada: o mesmo erro no mesmo lugar do código soma na contagem em vez de repetir. Toque para ver a pilha, quem estava usando, em que caso e o caminho completo da requisição.",
          ),
          p(
            "Marque visto, resolvido (se voltar, ele reabre sozinho e avisa de novo) ou ignorado (continua contando, sem e-mail). Erro novo chega no e-mail na hora; repetições, num resumo a cada 15 minutos. Sem o envio de e-mail configurado, a faixa vermelha avisa e “Mandar e-mail de teste” diz o motivo.",
          ),
        ],
      },
      {
        id: "telemetria",
        titulo: "Telemetria, IA, pessoas e traces",
        blocos: [
          p(
            "Telemetria: requisições por minuto, tempo de resposta por rota, taxa de erro, provedores e consultas lentas ao banco, no período escolhido. IA: chamadas, tokens, custo estimado e tempo por agente e motor, os freios que devolveram casos a uma pessoa e a linha do tempo do que cada agente fez. Pessoas: o que cada um fez, por pessoa, setor e caso. Traces: o caminho inteiro de uma requisição, em cascata.",
          ),
        ],
      },
    ],
  },
];
